import { Entry } from "contentful";
import Head from "next/head";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useMemo } from "react";

import Editorials from "~/types/editorials";
import { isActiveEntry } from "~/utils/editorial-utils";

type Props = {
  entry: Entry<Editorials.SeoMetadata>;
  titleSuffix?: string;
  canonical?: string;
  hreflangs?: { locale: string; url: string }[];
};

export default function SeoHead(props: Props) {
  const router = useRouter();
  const t = useTranslations();

  const noIndex = props.entry.fields?.no_index;
  const noFollow = props.entry.fields?.no_follow;

  const robots = useMemo(() => {
    if (noIndex && noFollow) {
      return "none";
    } else if (noIndex) {
      return "noindex";
    } else if (noFollow) {
      return "nofollow";
    } else {
      return "all";
    }
  }, [noIndex, noFollow]);

  if (!isActiveEntry(props.entry)) return null;

  const keywords = Array.isArray(props.entry.fields.keywords)
    ? props.entry.fields.keywords.join(", ")
    : props.entry.fields.keywords;

  const getTitle = () => {
    const titleSuffix = props.entry.fields.noTitleSuffix
      ? ""
      : ` | ${props.titleSuffix}`;

    return props.entry.fields.seoTitle + titleSuffix;
  };

  const getDescription = () => {
    return props.entry.fields.description;
  };

  return (
    <Head key={router.asPath}>
      <title>{getTitle()}</title>
      <meta
        name="description"
        content={`${getDescription()}. ${t("generic.buyNow")}`}
      />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      {props.canonical && <link rel="canonical" href={props.canonical} />}
      {props.hreflangs?.map((hreflang, index) => (
        <link
          key={index}
          rel="alternate"
          hrefLang={hreflang.locale}
          href={hreflang.url}
        />
      ))}
    </Head>
  );
}
